import { useEffect, useState } from 'react';
import { NarrationSection } from './NarrationSection';
import { useNavigate } from 'react-router-dom';
import styles from './ScenarioGenerator.module.css';
import { Tooltip } from '../../common/Tooltip';
import { ClickDropdown, HoverDropdownItem, HoverDropdownDivider } from '../../common/HoverDropdown';
import { ConfirmDialog } from '../../common/ConfirmDialog';
import { BgmSection } from './BgmSection';
import { ScenarioInfoModal } from './ScenarioInfoModal';
import { SceneItem } from './SceneItem';
import { StoryboardCandidates } from './StoryboardCandidates';
import { LoadingOverlay } from '../../shared/LoadingOverlay';
import { brandConfig } from '../../../config/brands';
import { useSceneStateContext } from './hooks/SceneStateContext';
import { useAutoVideoCardSegmentation } from './hooks/useAutoVideoCardSegmentation';
import { useUpdateScenario } from '../../../features/scenarios/hooks';
import type { CreditRatesResponse } from '../../../features/organizations/types';
import type { GenerationModel } from '../../../features/generation-models/api';
import type { VideoSettings } from './VideoGenerationSettings';
import type { Scenario, ScenarioResponse } from './types';
import { isLyricsModeScenario, type UseBgmEditorReturn } from './hooks/useBgmEditor';
import { isShellPolledInSession } from './hooks/useScenarioGeneration';
import { isMotionEngineScenario, isMotionPrepareShell } from './motionScenario';
import { MotionPrepareStagePanel } from './MotionPrepareStagePanel';
import { MotionScenarioPanel } from './MotionScenarioPanel';
import { ScriptImportReportSection } from './ScriptImportReportSection';
import { isImportedScenario, mergeImportReportIntoScenarios, readImportReport } from './scriptImport';
import type { UseScenarioInfoModalReturn } from './hooks/useScenarioInfoModal';
import type { UseScenarioTabsDragReturn } from './hooks/useScenarioTabsDrag';
import { ThreeDotsIcon } from '../../common/selectIcons';
import { isMotionV2Script } from '../../../features/scenarios/scriptContract';
import { NarrationDialogueSwapModal } from './NarrationDialogueSwapModal';
import { scenarioHasSwappableText } from './narrationDialogueSwap';
import { useSceneHandlers } from './hooks/SceneHandlersContext';

type VideoGenerationMode = 'frame' | 'reference' | 'storyboard' | null;

// タイトルカード/エンドカード生成中にシーン一覧へ差し込むスケルトン。
function SceneCardSkeleton({ label }: { label: string }) {
  return (
    <li className={styles.sceneSkeleton} aria-busy="true">
      <div className={styles.sceneSkeletonHeader}>
        <span className={styles.sceneSkeletonSpinner} aria-hidden="true" />
        <span className={styles.sceneSkeletonLabel}>{label}</span>
      </div>
      <div className={styles.sceneSkeletonThumb} aria-hidden="true" />
    </li>
  );
}

export interface WorkPanelProps {
  scenarios: Scenario[];
  activeScenarioId: string | null;
  mode: string;
  changePhase: (next: 'setup' | 'work') => void;
  setActiveScenarioId: (id: string | null) => void;
  scenarioHeadingRef: React.RefObject<HTMLHeadingElement | null>;

  handleClearScenario: () => void;
  setIsScenarioBookOpen: React.Dispatch<React.SetStateAction<boolean>>;

  // タブ
  tabsDrag: Pick<UseScenarioTabsDragReturn, 'scenarioTabsRef' | 'handleTabsMouseDown'>;
  handleTabClick: (e: React.MouseEvent, scenarioId: string) => void;
  handleCloseScenarioTab: (scenarioId: string) => void;
  // 各シナリオで生成ジョブが進行中か (scenarioId -> boolean)。タブの生成中インジケータ用。
  scenarioGeneratingMap: Record<string, boolean>;

  // ヘッダーアクション (currentScenario 用)
  scenarioInfoModal: UseScenarioInfoModalReturn;
  videoSettings: VideoSettings;
  videoGenerationMode: VideoGenerationMode;
  batchGeneratingFrames: Record<string, boolean>;
  setShowBatchCancelModal: React.Dispatch<React.SetStateAction<string | null>>;
  handleBatchGenerateFirstFrames: () => void;
  handleToggleStar: (id: string, current: boolean) => void;
  handleTogglePublish: (id: string, current: boolean) => void;
  isDownloading: boolean;
  handleDownloadScenario: (scenario: Scenario) => void;
  setShowClearAllFramesConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  handleCopyScenarioJson: (scenario: Scenario) => void;
  handleDeleteScenario: (id: string, title: string) => void | Promise<void>;
  handleDuplicateScenario: (scenarioId: string) => void | Promise<void>;

  // ConfirmDialogs
  showClearAllFramesConfirm: boolean;
  handleClearAllFrames: () => void;
  showBatchCancelModal: string | null;
  batchFrameCancelRefs: React.MutableRefObject<Record<string, boolean>>;

  // ScenarioInfoModal 用
  videoGenerationModels: GenerationModel[];
  lastResult: ScenarioResponse | null;
  setLastResult: React.Dispatch<React.SetStateAction<ScenarioResponse | null>>;
  setVideoSettings: React.Dispatch<React.SetStateAction<VideoSettings>>;
  updateScenarioMutation: ReturnType<typeof useUpdateScenario>;
  /** 現在のユーザー (app_users.user_uid)。シナリオ情報モーダルの編集可否 (所有者のみ) 判定に渡す。 */
  currentUserId?: string;

  // シーン追加ボタン + BgmSection
  setAddSceneModalScenarioId: React.Dispatch<React.SetStateAction<string | null>>;
  createSceneMutation: { isPending: boolean };
  bgm: UseBgmEditorReturn;
  creditRatesData: CreditRatesResponse | undefined;
  getSceneVideoSettings: (sceneKey: string) => {
    duration: number; generateAudio: boolean; videoModel?: string; resolution?: string;
    sceneRangeEnabled?: boolean; sceneRangeStart?: number; sceneRangeEnd?: number;
  };
  // ストーリーボードの動画カード (2枚目以降) を削除する。アンカーシーンの
  // video_material_id を外す (テイク素材自体は残る)。成功時 true。
  handleRemoveVideoCard: (sceneId: string, sceneKey: string) => Promise<boolean>;
  handleDownloadMaterial: (materialId: string, namePrefix: string) => void;
  // シナリオ設定 (SetupPanel キャスティング) の性別/年代フィルタ。俳優未選択時に
  // ストーリーボード生成プロンプトへ「主な登場人物」を出すため StoryboardCandidates
  // に渡す。
  castingGenderFilter?: string;
  castingAgeFilter?: number | null;
  // 選択中の素材を1枚のタイル画像にまとめてダウンロードする処理。ストーリーボードの
  // 「{商品}画像を添付する」直下の暫定DLアイコン (StoryboardCandidates) で使用する。
  handleDownloadAssetTile: () => void;
  isDownloadingAssetTile: boolean;
  // 「商品画像を再生成」: 選択中の素材から統合タイル画像を合成し直して一時素材に
  // アップロードする。以後のストーリーボード動画生成の商品画像添付に使われる。
  handleRegenerateProductTile: () => void;
  isRegeneratingProductTile: boolean;
  // シーン追加(全ルート)生成中のスケルトン。DB待ちが長いため、対象シナリオの
  // シーン一覧に position(start=先頭 / end=末尾)でプレースホルダーを差し込む。
  pendingSceneSkeletons?: SceneSkeletonInfo[];
  // 全編モーションの BGM 先行フロー (§10.5) で、シェルの本文ができたときの読み込み
  // (このタブのシェルフローがポーリング中なら呼ばれない)。
  onMotionShellReady?: (scenarioId: string) => void;
}

export type SceneSkeletonInfo = {
  scenarioId: string;
  position: 'start' | 'end';
  label: string;
};

export function WorkPanel(props: WorkPanelProps) {
  const navigate = useNavigate();
  const {
    scenarios, activeScenarioId, mode, changePhase, setActiveScenarioId, scenarioHeadingRef,
    handleClearScenario, setIsScenarioBookOpen,
    tabsDrag, handleTabClick, handleCloseScenarioTab, scenarioGeneratingMap,
    scenarioInfoModal, videoSettings, videoGenerationMode,
    batchGeneratingFrames,
    setShowBatchCancelModal,
    handleBatchGenerateFirstFrames,
    handleToggleStar, handleTogglePublish,
    isDownloading, handleDownloadScenario,
    setShowClearAllFramesConfirm, handleCopyScenarioJson, handleDeleteScenario, handleDuplicateScenario,
    showClearAllFramesConfirm, handleClearAllFrames,
    showBatchCancelModal, batchFrameCancelRefs,
    videoGenerationModels, lastResult, setLastResult, setVideoSettings,
    updateScenarioMutation,
    setAddSceneModalScenarioId, createSceneMutation,
    bgm, creditRatesData, getSceneVideoSettings, handleRemoveVideoCard, handleDownloadMaterial,
    castingGenderFilter, castingAgeFilter,
    handleDownloadAssetTile, isDownloadingAssetTile,
    handleRegenerateProductTile, isRegeneratingProductTile,
    pendingSceneSkeletons,
    onMotionShellReady,
  } = props;

  const { scenarioTabsRef, handleTabsMouseDown } = tabsDrag;

  // 「ファイルDL」のカスタムツールチップ (ホバーで出る .exportPopover) は、
  // ボタンをクリックしてもホバー/フォーカスが残るため出っぱなしになる。クリック後は
  // ポインタがアイコンから離れるまで非表示にする。
  const [exportTooltipDismissed, setExportTooltipDismissed] = useState(false);
  const [isDuplicating, setIsDuplicating] = useState(false);
  // 「セリフとナレーションを入れ替える」モーダルを開いているシナリオ (docs/features/scenario-narration-dialogue-swap-plan.md §3.9)
  const [swapModalScenarioId, setSwapModalScenarioId] = useState<string | null>(null);
  const { mentionActorSources } = useSceneHandlers();

  // ストーリーボードモードの動画カード。動画を持つ本編シーン (アンカーシーン) は
  // 自動でカード化されるため、ここでは「動画を追加」で増やした未生成カードの
  // アンカーシーン order だけを scenarioId ごとに保持する (生成完了後は DB 側の
  // video_material_id からカードが導出されるので永続化は不要)。
  const [extraVideoCardOrders, setExtraVideoCardOrders] = useState<Record<string, number[]>>({});
  // タイムライン総尺がモデル最大尺を超えるシナリオは、開いた時点で総尺が収まる数の
  // 動画カードを自動で並べる。各カードの「特定シーンだけ生成」範囲は getAutoSegment を
  // videoCardDefaultRangeStart/End として渡し、SceneItem のマウント時復元に適用させる。
  const { getAutoSegment } = useAutoVideoCardSegmentation({
    scenarios,
    activeScenarioId,
    videoGenerationMode,
    videoModels: videoGenerationModels,
    defaultVideoModel: (videoSettings as { videoModel?: string }).videoModel,
    extraVideoCardOrders,
    setExtraVideoCardOrders,
  });
  // 生成済み動画を持つカードの削除確認 (DB の video_material_id を外すため一度確認する)
  const [removeVideoCardConfirm, setRemoveVideoCardConfirm] = useState<{
    scenarioId: string; sceneId: string; sceneOrder: number; sceneKey: string; cardIndex: number;
  } | null>(null);
  // 動画カードの導出に使う共有 state (生成直後のローカル素材ID / 生成中フラグ)
  const { sceneVideoMaterialId, sceneVideoLoading, setAllScenesCollapsed } = useSceneStateContext();

  // 「すべて開く」⇔「すべて閉じる」のトグル表示。シーンカードは既定で開いている (マウント時) ので、
  // シナリオ切替でボタンも「すべて閉じる」に戻す。
  const [allScenesExpanded, setAllScenesExpanded] = useState(true);
  useEffect(() => {
    setAllScenesExpanded(true);
  }, [activeScenarioId]);
  const handleToggleAllScenes = () => {
    const next = !allScenesExpanded;
    setAllScenesExpanded(next);
    setAllScenesCollapsed(!next);
  };

  // ローカル追加カードの管理 state から order を取り除く (確定削除・未生成カード削除の共通処理)
  const dropExtraVideoCardOrder = (scenarioId: string, order: number) => {
    setExtraVideoCardOrders((prev) => ({
      ...prev,
      [scenarioId]: (prev[scenarioId] ?? []).filter((o) => o !== order),
    }));
  };

  return (
        <div className={`${styles.workPanel} ${styles.workPanelEnter}`}>
            {mode !== 'prompt' && scenarios.length > 0 && (
            <div className={styles.resultsSection}>
            <div className={styles.workHeader}>
              <button
                type="button"
                className={`${styles.backToSetupButton} ${styles.navButton}`}
                onClick={() => { changePhase('setup'); setActiveScenarioId(null); window.history.replaceState(null, '', '/studio/video'); }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6" /></svg>
                設定に戻る
              </button>
              <h2 ref={scenarioHeadingRef} style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0, color: 'rgba(245, 241, 230, 0.95)' }}>{brandConfig.labels.scenarioEdit}</h2>
              <div className={styles.workHeaderRight}>
                <button
                  type="button"
                  className="header-action-button"
                  onClick={handleClearScenario}
                  data-tooltip="すべてのシナリオを閉じてクリア"
                  data-tooltip-instant=""
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            </div>
            <div className={styles.scenarioTabsSection}>
            <div
              className={styles.scenarioTabs}
              ref={scenarioTabsRef}
              onMouseDown={handleTabsMouseDown}
            >
              <Tooltip content="シナリオブックを開く">
                <button
                  type="button"
                  className={styles.openBookTab}
                  onClick={() => setIsScenarioBookOpen(true)}
                >
                  <img src="/book_book_pages.svg" alt="" className={styles.openBookTabIcon} style={{ width: '18px', height: '18px' }} />
                  <span className={styles.openBookTabLabel}>開く</span>
                </button>
              </Tooltip>
              <span className={styles.scenarioTabsSeparator} aria-hidden="true" />
              {scenarios.map((scenario, index) => {
                const isActive = (activeScenarioId ?? scenarios[0]?.id) === scenario.id;
                const isGenerating = !!scenarioGeneratingMap[scenario.id];
                const hasRenderedVideo = scenario.scenes?.some((s) => !!s.video_material_id);
                const isComplete = !isGenerating && hasRenderedVideo;
                return (
                  <div
                    key={scenario.id}
                    className={`${styles.scenarioTab} ${isActive ? styles.scenarioTabActive : ''} ${isGenerating ? styles.scenarioTabGenerating : ''} ${scenarios.length > 1 ? styles.scenarioTabStagger : ''}`}
                    style={scenarios.length > 1 ? { animationDelay: `${index * 0.1}s` } : undefined}
                    onClick={(e) => handleTabClick(e, scenario.id)}
                    role="tab"
                    aria-selected={isActive}
                    title={scenario.title}
                  >
                    <span className={styles.scenarioTabNumber}>{index + 1}</span>
                    <span className={styles.scenarioTabTitle}>{scenario.title || `シナリオ ${index + 1}`}</span>
                    {isComplete && (
                      <span
                        className={styles.scenarioTabDot}
                        title="生成完了"
                        aria-label="生成完了"
                      />
                    )}
                    <Tooltip content="このシナリオを閉じる" className={styles.scenarioTabCloseWrap}>
                      <button
                        type="button"
                        className={styles.scenarioTabClose}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCloseScenarioTab(scenario.id);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </Tooltip>
                    {isGenerating && (
                      <span className={styles.scenarioTabProgress} aria-hidden="true">
                        <span className={styles.scenarioTabProgressBar} />
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {/* タグ + Scenario action buttons */}
            {(() => {
              const currentScenario = scenarios.find(s => s.id === (activeScenarioId ?? scenarios[0]?.id));
              if (!currentScenario) return null;

              return (
                <div className={styles.scenarioTagsAndActions}>
                  {(currentScenario.summary || (currentScenario.tags && currentScenario.tags.length > 0)) && (
                    <div className={styles.scenarioSummaryAndTags}>
                      {currentScenario.tags && currentScenario.tags.length > 0 && (
                        <div className={styles.scenarioTags}>
                          {currentScenario.tags.map((tag, tagIdx) => (
                            <span key={tagIdx} className="badge badge-gray scenarioTag">{tag}</span>
                          ))}
                        </div>
                      )}
                      {currentScenario.summary && (
                        <span className={styles.scenarioSummaryText} title={currentScenario.summary}>{currentScenario.summary}</span>
                      )}
                    </div>
                  )}
                <div className={styles.scenarioActionsCompact}>
                  <button
                    type="button"
                    className="header-action-button"
                    onClick={handleToggleAllScenes}
                    data-tooltip={allScenesExpanded ? 'すべてのシーンのパネルを折り畳む' : 'すべてのシーンのパネルを展開'}
                    data-tooltip-instant=""
                  >
                    {allScenesExpanded ? (
                      <img src="/move_collapse_vertical.svg" alt="すべて閉じる" style={{ width: '16px', height: '16px' }} />
                    ) : (
                      <img src="/move_expand_vertical.svg" alt="すべて開く" style={{ width: '16px', height: '16px' }} />
                    )}
                  </button>

                  <button
                    type="button"
                    className="header-action-button"
                    onClick={() => scenarioInfoModal.open(currentScenario, videoSettings)}
                    data-tooltip="シナリオ情報"
                    data-tooltip-instant=""
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>

                  {/* ストーリーボードモードはフレーム自体を使わないのでボタンごと非表示 */}
                  {videoGenerationMode !== 'storyboard' && (
                    <button
                      type="button"
                      className={`header-action-button ${batchGeneratingFrames[currentScenario.id] ? styles.batchGeneratingButton : ''}`}
                      disabled={videoGenerationMode === 'reference'}
                      onClick={() => {
                        if (batchGeneratingFrames[currentScenario.id]) {
                          setShowBatchCancelModal(currentScenario.id);
                        } else {
                          handleBatchGenerateFirstFrames();
                        }
                      }}
                      data-tooltip={batchGeneratingFrames[currentScenario.id] ? undefined : videoGenerationMode === 'reference' ? '参照画像モードでは利用できません' : 'フレームをすべて生成'}
                      data-tooltip-instant=""
                      title={batchGeneratingFrames[currentScenario.id] ? 'フレーム一括生成をキャンセル' : 'フレームをすべて生成'}
                      style={videoGenerationMode === 'reference' ? { cursor: 'not-allowed' } : batchGeneratingFrames[currentScenario.id] ? { cursor: 'pointer', pointerEvents: 'auto' } : undefined}
                    >
                      {batchGeneratingFrames[currentScenario.id] ? (
                        <>
                          <img src="/picture_stack_landscape.svg" alt="フレーム一括生成" style={{ width: '16px', height: '16px' }} />
                          <span className={styles.batchLabelStack}>
                            <span className={styles.batchGeneratingLabel}>フレーム生成中...</span>
                            <span className={styles.batchCancelLabel}>一括生成を停止</span>
                          </span>
                        </>
                      ) : (
                        <img src="/picture_stack_landscape.svg" alt="フレーム一括生成" style={{ width: '16px', height: '16px', opacity: videoGenerationMode === 'reference' ? 0.3 : undefined }} />
                      )}
                    </button>
                  )}

                  <span className={styles.actionsDivider} />

                  <button
                    type="button"
                    className={`header-action-button ${currentScenario.starred ? 'active' : ''}`}
                    onClick={() => handleToggleStar(currentScenario.id, currentScenario.starred || false)}
                    data-tooltip={currentScenario.starred ? 'お気に入りから外す' : 'お気に入りに追加'}
                    data-tooltip-instant=""
                  >
                    <img src="/rating_star_ribbon.svg" alt="お気に入り" style={{ width: '16px', height: '16px' }} />
                  </button>

                  <button
                    type="button"
                    className={`header-action-button ${currentScenario.published ? 'active' : ''}`}
                    onClick={() => handleTogglePublish(currentScenario.id, currentScenario.published || false)}
                    data-tooltip={currentScenario.published ? '共有を解除する' : '組織に共有する'}
                    data-tooltip-instant=""
                  >
                    <img src="/common_file_give_hand_3.svg" alt="共有" style={{ width: '16px', height: '16px' }} />
                  </button>

                  {/* エクスポートの導線は末尾の「動画をエクスポート」メニューに一本化 (上部アイコンは撤去済み) */}

                  <ClickDropdown
                    trigger={
                      <button
                        type="button"
                        className="header-action-button"
                      >
                        <ThreeDotsIcon vertical />
                      </button>
                    }
                    position="bottom"
                    align="right"
                    minWidth="220px"
                  >
                    <HoverDropdownItem onClick={() => setShowClearAllFramesConfirm(true)} disabled={videoGenerationMode === 'reference' || videoGenerationMode === 'storyboard'}>
                      フレームをすべてクリア
                    </HoverDropdownItem>
                    <HoverDropdownItem
                      onClick={() => setSwapModalScenarioId(currentScenario.id)}
                      disabled={
                        swapModalScenarioId !== null
                        || currentScenario.status === 'generating'
                        || isMotionV2Script(currentScenario)
                        || !scenarioHasSwappableText(currentScenario)
                      }
                    >
                      セリフとナレーションを入れ替える
                    </HoverDropdownItem>
                    <HoverDropdownItem onClick={() => handleCopyScenarioJson(currentScenario)} icon={<img src="/duplicate.svg" alt="" style={{ width: '14px', height: '14px' }} />}>
                      クリップボードにコピー
                    </HoverDropdownItem>
                    <HoverDropdownItem
                      onClick={async () => {
                        setIsDuplicating(true);
                        try {
                          await handleDuplicateScenario(currentScenario.id);
                        } finally {
                          setIsDuplicating(false);
                        }
                      }}
                      disabled={isDuplicating || currentScenario.status === 'generating'}
                      icon={<img src="/duplicate.svg" alt="" style={{ width: '14px', height: '14px' }} />}
                    >
                      シナリオを複製
                    </HoverDropdownItem>
                    <HoverDropdownDivider />
                    <HoverDropdownItem onClick={() => handleDeleteScenario(currentScenario.id, currentScenario.title)} icon={<img src="/bin_1.svg" alt="" style={{ width: '14px', height: '14px' }} />}>
                      <span style={{ color: 'var(--color-danger, #ef4444)' }}>シナリオを削除</span>
                    </HoverDropdownItem>
                  </ClickDropdown>
                </div>
                </div>
              );
            })()}

            {/* フレームをすべてクリア確認モーダル */}
            <ConfirmDialog
              isOpen={showClearAllFramesConfirm}
              title=""
              message={<>すべてのフレーム（最初のフレーム・最後のフレーム）をシナリオから外します。素材自体は削除されません。</>}
              confirmLabel="クリア"
              cancelLabel="キャンセル"
              variant="danger"
              buttonsAlign="space-between"
              onConfirm={() => {
                handleClearAllFrames();
                setShowClearAllFramesConfirm(false);
              }}
              onCancel={() => setShowClearAllFramesConfirm(false)}
            />

            {/* セリフとナレーションを入れ替えるモーダル */}
            {(() => {
              const swapScenario = swapModalScenarioId ? scenarios.find((s) => s.id === swapModalScenarioId) : undefined;
              if (!swapScenario) return null;
              return (
                <NarrationDialogueSwapModal
                  isOpen
                  onClose={() => setSwapModalScenarioId(null)}
                  scenario={swapScenario}
                  setLastResult={setLastResult}
                  actors={mentionActorSources}
                />
              );
            })()}

            {/* フレーム一括生成キャンセル確認モーダル */}
            <ConfirmDialog
              isOpen={showBatchCancelModal !== null}
              title=""
              message={<>現在生成中の画像以降の生成をキャンセルします。</>}
              confirmLabel="はい"
              cancelLabel="いいえ"
              variant="danger"
              buttonsAlign="space-between"
              onConfirm={() => {
                if (showBatchCancelModal) {
                  batchFrameCancelRefs.current[showBatchCancelModal] = true;
                }
                setShowBatchCancelModal(null);
              }}
              onCancel={() => setShowBatchCancelModal(null)}
            />



            {/* 動画カード削除確認モーダル (生成済み動画があるカードのみ)。
                シーンの video_material_id を外すだけで、テイク素材自体は削除されない */}
            <ConfirmDialog
              isOpen={removeVideoCardConfirm !== null}
              title=""
              message={
                <>
                  動画{removeVideoCardConfirm?.cardIndex}のカードを削除します。
                  生成済みの動画素材は削除されず、カードを追加し直すと動画履歴から選び直せます。
                </>
              }
              confirmLabel="削除"
              cancelLabel="キャンセル"
              variant="danger"
              buttonsAlign="space-between"
              onConfirm={async () => {
                const target = removeVideoCardConfirm;
                if (!target) return;
                const ok = await handleRemoveVideoCard(target.sceneId, target.sceneKey);
                if (ok) {
                  dropExtraVideoCardOrder(target.scenarioId, target.sceneOrder);
                }
                setRemoveVideoCardConfirm(null);
              }}
              onCancel={() => setRemoveVideoCardConfirm(null)}
            />

            <ScenarioInfoModal
              modal={scenarioInfoModal}
              scenarios={scenarios}
              activeScenarioId={activeScenarioId}
              videoGenerationModels={videoGenerationModels}
              lastResult={lastResult}
              setLastResult={setLastResult}
              setVideoSettings={setVideoSettings}
              updateScenarioMutation={updateScenarioMutation}
              currentUserId={props.currentUserId}
            />

            {scenarios.map((scenario) => {
              const isActive = (activeScenarioId ?? scenarios[0]?.id) === scenario.id;
              if (!isActive) {
                return null;
              }
              // シーン一覧 + シーン追加ボタン。ストーリーボードモードでは
              // StoryboardCandidates の右カラム（children）として描画する。
              const startSkeletons = pendingSceneSkeletons?.filter((s) => s.scenarioId === scenario.id && s.position === 'start') ?? [];
              const endSkeletons = pendingSceneSkeletons?.filter((s) => s.scenarioId === scenario.id && s.position === 'end') ?? [];
              // ランダム生成のシェル: 本文未生成 (status='generating' かつ scenes 空) の間は
              // シーンごとのスケルトンは並べず、共通ローディング 1 つだけを出す (本文到着で実体に差し替わる)。
              // シーン追加導線も隠す。
              const isShellGenerating = scenario.status === 'generating' && scenario.scenes.length === 0;
              const sceneListContent = (
                <>
                  {scenario.scenes.length || startSkeletons.length || endSkeletons.length ? (
                    <ol className={styles.sceneList}>
                      {startSkeletons.map((s, i) => <SceneCardSkeleton key={`start-skeleton-${i}`} label={s.label} />)}
                      {scenario.scenes.map((scene, sceneIndex) => {
                        const sceneKey = `${scenario.id}-scene-${scene.order}`;
                        return (
                          <SceneItem
                            key={sceneKey}
                            scenario={scenario}
                            scene={scene}
                            sceneIndex={sceneIndex}
                            sceneKey={sceneKey}
                            isFirstScene={sceneIndex === 0}
                            isLastScene={sceneIndex === scenario.scenes.length - 1}
                            nextSceneContinuesPrevious={sceneIndex < scenario.scenes.length - 1 && !!(scenario.scenes[sceneIndex + 1]?.continues_previous_shot)}
                          />
                        );
                      })}
                      {endSkeletons.map((s, i) => <SceneCardSkeleton key={`end-skeleton-${i}`} label={s.label} />)}
                    </ol>
                  ) : null}

                  {/* シーン追加: メイン導線ではないため控えめなゴースト行に */}
                  <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
                    <button
                      type="button"
                      className={styles.addSceneGhost}
                      onClick={() => setAddSceneModalScenarioId(scenario.id)}
                      disabled={createSceneMutation.isPending}
                    >
                      <span className={styles.addSceneGhostPlus} aria-hidden="true">+</span>
                      シーンを追加
                    </button>
                  </div>
                </>
              );

              // 歌モノは先に曲を作ってから動画を生成するため、BGM を動画より前に置く。
              // 全編モーション (§10.5) も BGM が先 (小節に合わせて尺を決める) なので前に置く。
              const isMotionEngine = isMotionEngineScenario(scenario);
              const bgmBeforeVideo = isLyricsModeScenario(scenario) || isMotionEngine;

              // ナレーション セクション (話者ごとの声と、行の読み上げ = handleGenerateVoiceLines)。歌モノでは内部で非表示になる。
              const narrationSectionEl = (
                <NarrationSection
                  scenario={scenario}
                  bgmIsVocalLead={bgm.bgmIsVocalLead}
                />
              );

              // BGM セクション
              const bgmSectionEl = (
                <>
                  <BgmSection
                    scenario={scenario}
                    bgm={bgm}
                    lastResult={lastResult}
                    setLastResult={setLastResult}
                    creditRatesData={creditRatesData}
                    updateScenarioMutation={updateScenarioMutation}
                    getSceneVideoSettings={getSceneVideoSettings}
                    handleDownloadMaterial={handleDownloadMaterial}
                  />
                  {bgmBeforeVideo && <div className={`${styles.scenarioExportArrow} ${styles.scenarioCardArrow}`} aria-hidden="true" />}
                </>
              );

              // シナリオ末尾の仕上げ導線。囲い枠や見出しは設けず、▼ の下に動画エディタへの CTA
              // (サブカラーの btn-modal-sub) と「ファイルDL」(ZIP 一括。背景なしのアイコンのみ) の
              // 2 つだけを置く。プレビュー動画 / 連結プレビュー / YouTube / XML / FCPXML / Canva の
              // 各出力はここから撤去した (YouTube・XML・FCPXML は動画エディタ側で書き出す)。
              // 動画かフレーム画像を持つシーンが1つでもあるか。ストーリーボードモードで
              // 動画未生成 (絵コンテは scenario.storyboards 側でシーンには紐づかない) や、
              // シーンごとモードでフレーム画像も動画も無いシナリオでは false になり、
              // 動画エディタ (タイムラインが空になる) を非活性にする。
              const hasSceneMedia = !!scenario.scenes?.some(s => s.video_material_id || s.image_material_id);

              const exportSectionEl = (
                <div className={styles.scenarioActions}>
                  <div className={styles.scenarioFinish}>
                    <div className={styles.scenarioExportArrow} aria-hidden="true" />
                    <div className={styles.scenarioFinishRow}>
                      {/* 非活性時はボタン自体がホバーイベントを受けないため、ラッパーの
                          :hover でカスタムツールチップ (exportPopover) を出す。 */}
                      <div className={styles.scenarioEditorCtaWrap}>
                        <button
                          type="button"
                          className={`btn-modal-sub ${styles.scenarioEditorCta}`}
                          onClick={() => navigate(`/studio/video/${scenario.id}/editor`)}
                          disabled={!hasSceneMedia}
                          title={hasSceneMedia ? 'タイムライン形式の編集画面を開きます。シーンの並べ替えやトリム、テロップ・ナレーション・BGMの調整をして動画を書き出せます' : undefined}
                        >
                          <img src="/video_player_slider.svg" alt="" aria-hidden="true" className={styles.scenarioEditorCtaIcon} />
                          <span className={styles.scenarioEditorCtaText}>
                            <span className={styles.scenarioEditorCtaLabel}>動画エディタ</span>
                            <span className={styles.scenarioEditorCtaSub}>タイムラインで仕上げて書き出す</span>
                          </span>
                        </button>
                        {!hasSceneMedia && (
                          <div className={styles.exportPopover} role="tooltip">
                            <p className={styles.exportPopoverTitle}>動画やフレーム画像が生成されていません</p>
                          </div>
                        )}
                      </div>

                      <div
                        className={`${styles.exportIconWrapper} ${exportTooltipDismissed ? styles.exportIconWrapperDismissed : ''}`}
                        onMouseLeave={() => setExportTooltipDismissed(false)}
                      >
                        <button
                          type="button"
                          className={styles.scenarioDlBtn}
                          onClick={(e) => {
                            e.currentTarget.blur();
                            setExportTooltipDismissed(true);
                            handleDownloadScenario(scenario);
                          }}
                          disabled={isDownloading}
                          aria-label="ファイルを一括ダウンロード"
                        >
                          <img src="/zip_file_download.svg" alt="" aria-hidden="true" />
                        </button>
                        <div className={styles.exportPopover}>
                          <p className={styles.exportPopoverTitle}>ファイルを一括ダウンロード</p>
                          <p className={styles.exportPopoverDesc}>全シーンのMP4・MP3素材をZIPにまとめてダウンロードします。CapCutやiMovieで編集する場合はこちら</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );

              // ランダム生成のシェル待ち (本文未到着) は、ストーリーボード等のレイアウト
              // (黒い動画ボックス領域など) を描かず共通ローディング 1 つだけを出す。
              // 全編モーションの BGM 先行フロー: BGM 生成中 / 確認画面 / 台本生成中を出す
              if (isShellGenerating && isMotionPrepareShell(scenario)) {
                return (
                  <div key={scenario.id} className={styles.scenarioContentFadeIn}>
                    <MotionPrepareStagePanel
                      scenario={scenario}
                      isPolledElsewhere={isShellPolledInSession}
                      onReady={(id) => onMotionShellReady?.(id)}
                    />
                  </div>
                );
              }
              if (isShellGenerating) {
                return (
                  <div key={scenario.id} className={styles.scenarioContentFadeIn}>
                    <div className={styles.shellLoading}>
                      <LoadingOverlay message="生成中…" transparent />
                    </div>
                  </div>
                );
              }

              // 台本取り込みのシナリオ (origin='imported') は先頭に取り込みレポートを出す (§4.3)。
              // scenes の有無に依存させない (0 シーンでも描画できる)。
              const importReportEl = (isImportedScenario(scenario.generation_metadata) || readImportReport(scenario.generation_metadata)) ? (
                <ScriptImportReportSection
                  scenario={scenario}
                  onReportUpdated={(report) => {
                    // 応答は完全な import_report。既存にあって応答に無いキーは残す (mergeImportReportIntoScenarios)
                    setLastResult((prev) => (prev
                      ? { ...prev, scenarios: mergeImportReportIntoScenarios(prev.scenarios, scenario.id, report) }
                      : prev));
                  }}
                />
              ) : null;

              // ストーリーボードの動画カード (アンカーシーン) 一覧。動画カードの描画と、
              // 添付設定 UI の個別添付判定 (カードごとの実効モデル) の両方で使う。
              const storyboardCards = videoGenerationMode === 'storyboard' ? (() => {
                // ストーリーボードの本編動画カード群。各カードは担当範囲の開始シーン
                // (アンカーシーン) に紐づき、生成した動画はそのシーンの
                // video_material_id に保存される。ブランドカット (タイトル/エンド
                // カード) は本編動画の対象外なので除外する。
                const bodyScenes = scenario.scenes.filter(
                  (s) => s.brand_cut_position !== 'start' && s.brand_cut_position !== 'end',
                );
                const fallbackScene = bodyScenes[0] ?? scenario.scenes[0];
                if (!fallbackScene) return null;

                const sceneKeyOf = (order: number) => `${scenario.id}-scene-${order}`;
                const extraOrders = extraVideoCardOrders[scenario.id] ?? [];
                const anchorOrderSet = new Set<number>();
                // カード1: 最初の本編シーン (従来の単独カードと同じアンカー)
                anchorOrderSet.add(fallbackScene.order);
                // 動画を持つ本編シーン (DB or 生成直後のローカル state) は自動でカード化
                for (const s of bodyScenes) {
                  if (s.video_material_id || sceneVideoMaterialId[sceneKeyOf(s.order)]) {
                    anchorOrderSet.add(s.order);
                  }
                }
                // 「動画を追加」でローカルに追加した未生成カード
                for (const order of extraOrders) {
                  if (bodyScenes.some((s) => s.order === order)) anchorOrderSet.add(order);
                }
                const anchorScenes = (bodyScenes.length > 0 ? bodyScenes : [fallbackScene])
                  .filter((s) => anchorOrderSet.has(s.order));

                // 次にカードを追加するときのアンカー: 既存カードの担当範囲
                // (設定中の範囲 > 保存済み範囲 > アンカー自身) の次の本編シーン。
                // 後続が無ければ未カード化の本編シーンにフォールバックする。
                let lastCoveredOrder = 0;
                for (const a of anchorScenes) {
                  const st = getSceneVideoSettings(sceneKeyOf(a.order));
                  const savedEnd = Number(
                    (a.generation_metadata as { scene_range?: { end?: number } } | undefined)
                      ?.scene_range?.end ?? NaN,
                  );
                  const end = st.sceneRangeEnabled && st.sceneRangeEnd != null
                    ? st.sceneRangeEnd
                    : Number.isFinite(savedEnd) ? savedEnd : a.order;
                  lastCoveredOrder = Math.max(lastCoveredOrder, end, a.order);
                }
                const nextAnchorOrder = (
                  bodyScenes.find((s) => s.order > lastCoveredOrder && !anchorOrderSet.has(s.order))
                  ?? bodyScenes.find((s) => !anchorOrderSet.has(s.order))
                )?.order ?? null;

                return { anchorScenes, nextAnchorOrder, sceneKeyOf };
              })() : null;

              return (
                <div key={scenario.id} className={styles.scenarioContentFadeIn}>
                  {importReportEl}
                  {/* ストーリーボードモード: 左列にストーリーボードのカード + 絵コンテ候補列、
                      右列にシーン一覧 + 動画カード + BGM + エクスポート (歌モノは BGM が動画より前)。動画カードは既定で
                      1枚だが「動画を追加」でシーン範囲ごとに分割できる (長尺対応)。
                      右列の「シーンを追加」の下に置く */}
                  {videoGenerationMode === 'storyboard' ? (
                    <StoryboardCandidates
                      scenario={scenario}
                      creditRatesData={creditRatesData}
                      castingGenderFilter={castingGenderFilter}
                      castingAgeFilter={castingAgeFilter}
                      handleDownloadAssetTile={handleDownloadAssetTile}
                      isDownloadingAssetTile={isDownloadingAssetTile}
                      handleRegenerateProductTile={handleRegenerateProductTile}
                      isRegeneratingProductTile={isRegeneratingProductTile}
                      videoCardModels={storyboardCards?.anchorScenes.map((a) => (
                        getSceneVideoSettings(storyboardCards.sceneKeyOf(a.order)).videoModel
                        || (videoSettings as { videoModel?: string }).videoModel
                      ))}
                      videoBox={(() => {
                        if (!storyboardCards) return undefined;
                        const { anchorScenes, nextAnchorOrder, sceneKeyOf } = storyboardCards;
                        return (disabledReason: string | null) => (
                          <>
                            {anchorScenes.map((anchorScene, cardIdx) => {
                              const anchorIndex = scenario.scenes.indexOf(anchorScene);
                              const k = sceneKeyOf(anchorScene.order);
                              const hasVideo = !!(anchorScene.video_material_id || sceneVideoMaterialId[k]);
                              // 2枚目以降のカードは削除できる (カード1は基点なので不可)。
                              // 生成中は動画がまだシーンに紐付いていないため削除させない。
                              const isRemovable = cardIdx > 0 && !sceneVideoLoading[k];
                              return (
                                <div key={k} style={cardIdx > 0 ? { marginTop: '12px' } : undefined}>
                                  <SceneItem
                                    videoBoxOnly
                                    videoBoxDisabledReason={disabledReason ?? undefined}
                                    scenario={scenario}
                                    scene={anchorScene}
                                    sceneIndex={anchorIndex}
                                    sceneKey={k}
                                    isFirstScene={anchorIndex === 0}
                                    isLastScene={false}
                                    nextSceneContinuesPrevious={false}
                                    videoCardIndex={cardIdx + 1}
                                    videoCardCount={anchorScenes.length}
                                    videoCardOrders={anchorScenes.map((s) => s.order)}
                                    videoCardDefaultRangeStart={
                                      getAutoSegment(scenario.id, anchorScene.order)?.start
                                      ?? (cardIdx > 0 ? anchorScene.order : undefined)
                                    }
                                    videoCardDefaultRangeEnd={getAutoSegment(scenario.id, anchorScene.order)?.end}
                                    onRemoveVideoCard={isRemovable ? () => {
                                      if (hasVideo) {
                                        // 生成済み動画があるカードは DB の紐付け解除を伴うため確認を挟む
                                        setRemoveVideoCardConfirm({
                                          scenarioId: scenario.id,
                                          sceneId: anchorScene.id,
                                          sceneOrder: anchorScene.order,
                                          sceneKey: k,
                                          cardIndex: cardIdx + 1,
                                        });
                                      } else {
                                        // 未生成のローカル追加カードはその場で取り除く
                                        dropExtraVideoCardOrder(scenario.id, anchorScene.order);
                                      }
                                    } : undefined}
                                  />
                                </div>
                              );
                            })}
                            {/* 動画を追加: 長い尺をモデルの最大尺 (Gemini OMNI 10秒 /
                                Seedance 30秒など) 内で複数の動画に分割するための追加カード。
                                担当シーンは各カードの「特定シーンだけ生成」で選ぶ */}
                            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
                              <button
                                type="button"
                                className={styles.addSceneGhost}
                                onClick={() => {
                                  if (nextAnchorOrder == null) return;
                                  setExtraVideoCardOrders((prev) => {
                                    const current = prev[scenario.id] ?? [];
                                    if (current.includes(nextAnchorOrder)) return prev;
                                    return { ...prev, [scenario.id]: [...current, nextAnchorOrder] };
                                  });
                                }}
                                disabled={nextAnchorOrder == null}
                                title={nextAnchorOrder == null ? '追加できるシーンがありません' : undefined}
                              >
                                <span className={styles.addSceneGhostPlus} aria-hidden="true">+</span>
                                動画を追加
                              </button>
                            </div>
                          </>
                        );
                      })()}
                      beforeVideoSection={bgmBeforeVideo ? bgmSectionEl : undefined}
                      afterVideoSection={<>{narrationSectionEl}{!bgmBeforeVideo && bgmSectionEl}{exportSectionEl}</>}
                    >
                      {sceneListContent}
                    </StoryboardCandidates>
                  ) : (
                    isMotionEngine ? (
                      // 全編モーション: BGM → ナレーション → 動画 の順 (小節とナレーションに動きを合わせるため先に作る)
                      <>
                        {bgmSectionEl}
                        {narrationSectionEl}
                        <div className={`${styles.scenarioExportArrow} ${styles.scenarioCardArrow}`} aria-hidden="true" />
                        <MotionScenarioPanel scenario={scenario} />
                        {sceneListContent}
                        {exportSectionEl}
                      </>
                    ) : (
                      <>
                        {bgmBeforeVideo && bgmSectionEl}
                        {sceneListContent}
                        {narrationSectionEl}
                        {!bgmBeforeVideo && bgmSectionEl}
                        {exportSectionEl}
                      </>
                    )
                  )}
                </div>
              );
            })}
          </div>

            </div>
            )}
          </div>
  );
}
